"use client"

import { useRef, useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { Upload } from "lucide-react"
import { triggerUpload } from "@/lib/upload/upload-bridge"

/**
 * Mounts the hidden <input type="file"> the rest of the app pokes via the
 * `open-global-upload` window event, AND wires page-wide drag-and-drop so
 * users can drop a CSV anywhere in the dashboard instead of going through
 * the modal → picker round-trip.
 *
 * Scoped to `/dashboard*` paths so the landing page, /try, /auth, etc. are
 * not affected. Honors `event.defaultPrevented` so nested drop zones
 * (CompareClientSection, MapperClient, /try) keep handling their own
 * drops without us double-firing.
 */
export default function GlobalUploadInput() {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const pathname = usePathname()

  // Click-the-picker bridge — preserved verbatim so the global "Upload CSV"
  // button in the header/welcome banner keeps working unchanged.
  useEffect(() => {
    const open = () => inputRef.current?.click()
    window.addEventListener("open-global-upload", open)
    return () => window.removeEventListener("open-global-upload", open)
  }, [])

  // Page-wide drag-and-drop — only active on dashboard routes.
  useEffect(() => {
    if (!pathname?.startsWith("/dashboard")) return

    // dragenter / dragleave fire for every nested element, so we count
    // entries and only flip the overlay on net-zero / first-enter.
    let dragCounter = 0

    const isFileDrag = (e: DragEvent) =>
      e.dataTransfer?.types?.includes("Files") ?? false

    const isAcceptableFile = (file: File) => {
      const name = file.name.toLowerCase()
      return (
        name.endsWith(".csv") ||
        name.endsWith(".txt") ||
        file.type === "text/csv" ||
        file.type === "application/csv" ||
        file.type === "text/plain"
      )
    }

    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      dragCounter++
      if (dragCounter === 1) setIsDragging(true)
    }

    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      dragCounter = Math.max(0, dragCounter - 1)
      if (dragCounter === 0) setIsDragging(false)
    }

    // Required to enable the drop event; if we don't preventDefault on
    // dragover, the browser opens the file natively instead.
    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      e.preventDefault()
    }

    const onDrop = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      // Local drop zones (CompareClientSection, MapperClient, /try) all
      // call e.preventDefault() in their own React onDrop. The native
      // event we receive here runs after the React event in bubble phase,
      // so checking defaultPrevented reliably tells us whether someone
      // downstream already claimed this drop. If so, just clean up our
      // overlay state and bail.
      const handledDownstream = e.defaultPrevented
      dragCounter = 0
      setIsDragging(false)
      if (handledDownstream) return

      e.preventDefault()
      const file = e.dataTransfer?.files?.[0]
      if (!file) return
      if (!isAcceptableFile(file)) return
      // Dismiss the choose-import-method modal if it happened to be open.
      // Without this the modal lingers in front of the upload progress
      // card after the user drops a file, which reads as a UI bug.
      window.dispatchEvent(new Event("close-upload-modal"))
      triggerUpload(file, { source: "dashboard" })
    }

    window.addEventListener("dragenter", onDragEnter)
    window.addEventListener("dragleave", onDragLeave)
    window.addEventListener("dragover", onDragOver)
    window.addEventListener("drop", onDrop)

    return () => {
      window.removeEventListener("dragenter", onDragEnter)
      window.removeEventListener("dragleave", onDragLeave)
      window.removeEventListener("dragover", onDragOver)
      window.removeEventListener("drop", onDrop)
    }
  }, [pathname])

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv,text/plain,application/csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          triggerUpload(file)
          // Reset so re-selecting the same file fires onChange again.
          e.target.value = ""
        }}
      />

      {isDragging && (
        <div
          aria-hidden
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none animate-in fade-in duration-150"
          style={{
            background: "rgba(15,81,50,0.10)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        >
          <div
            className="rounded-2xl px-9 py-7 flex flex-col items-center gap-3"
            style={{
              background: "#FFFFFF",
              border: "2px dashed #0F5132",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.65) inset, 0 30px 80px -20px rgba(10,10,10,0.30), 0 0 60px -28px rgba(15,81,50,0.40)",
            }}
          >
            {/* Mint hairline accent — Aperture-Light signature */}
            <div
              aria-hidden
              className="absolute top-0 inset-x-8 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(15,81,50,0.45), transparent)",
              }}
            />
            <div
              className="flex items-center justify-center"
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: "rgba(15,81,50,0.10)",
                border: "1px solid rgba(15,81,50,0.25)",
                color: "#0F5132",
              }}
            >
              <Upload size={24} />
            </div>
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0A0A0A",
                letterSpacing: "-0.005em",
              }}
            >
              Drop your CSV anywhere
            </p>
            <p style={{ fontSize: 13, color: "rgba(10,10,10,0.55)" }}>
              We&rsquo;ll start cleaning immediately.
            </p>
          </div>
        </div>
      )}
    </>
  )
}
